// src/components/BusyBox/BusyBox.tsx
import {
  Box,
  Paper,
  useTheme
} from "@mui/material";
import { BusyCircularProgress, BusyLinearProgress } from "@xylabs/react-shared";

// src/hooks/useBusyTiming.tsx
import { delay } from "@xylabs/delay";
import { useAsyncEffect } from "@xylabs/react-async-effect";
import {
  useCallback,
  useMemo,
  useState
} from "react";
var useBusyTiming = (busy, busyMinimum = 0) => {
  const [internalBusy, setInternalBusy] = useState(false);
  const [busyStart, setBusyStart] = useState(0);
  const timer = useMemo(
    () => ({
      evaluate: !busy && busyStart > 0,
      initialize: busy && busyStart === 0,
      terminated: !busy && busyStart === 0
    }),
    [busy, busyStart]
  );
  const evaluateTimer = useCallback(
    async (mounted) => {
      const busyDuration = Date.now() - busyStart;
      if (busyDuration < busyMinimum) {
        await delay(busyMinimum - busyDuration);
        if (!busy && internalBusy && mounted()) {
          setBusyStart(0);
          setInternalBusy(false);
        }
      } else if (mounted()) {
        setBusyStart(0);
      }
    },
    [busy, busyMinimum, busyStart, internalBusy]
  );
  useAsyncEffect(
    async (mounted) => {
      if (mounted() && busyMinimum === 0) {
        setInternalBusy(!!busy);
        return;
      }
      if (busyMinimum) {
        const {
          initialize,
          evaluate,
          terminated
        } = timer;
        if (initialize && mounted()) {
          setBusyStart(Date.now());
          setInternalBusy(true);
          return;
        }
        if (evaluate) {
          await evaluateTimer(mounted);
          return;
        }
        if (terminated) {
          setInternalBusy(false);
          return;
        }
      }
    },
    [busy, busyStart, busyMinimum, internalBusy, evaluateTimer, timer]
  );
  return internalBusy;
};

// src/components/BusyBox/BusyBox.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var BusyBox = ({
  background,
  children,
  busyVariant = "circular",
  busySize,
  busyOpacity = 0.85,
  busyColor,
  busy,
  busyCircularProps,
  busyLinearProps,
  busyMinimum = 0,
  paper,
  style,
  ...props
}) => {
  const theme = useTheme();
  const internalBusy = useBusyTiming(busy, busyMinimum);
  return /* @__PURE__ */ jsxs(
    Box,
    {
      component: paper ? Paper : "div",
      position: "relative",
      style: background ? {
        backgroundColor: theme.vars.palette.background.default,
        color: theme.vars.palette.text.primary,
        ...style
      } : style,
      ...props,
      children: [
        children,
        internalBusy && busyVariant === "linear" ? /* @__PURE__ */ jsx(BusyLinearProgress, { color: busyColor, opacity: busyOpacity, ...busyLinearProps }) : null,
        internalBusy && busyVariant === "circular" ? /* @__PURE__ */ jsx(BusyCircularProgress, { color: busyColor, opacity: busyOpacity, size: busySize, ...busyCircularProps }) : null
      ]
    }
  );
};
BusyBox.displayName = "BusyBoxXYLabs";

// src/components/BusyCard.tsx
import { Card } from "@mui/material";
import {
  BusyCircularProgress as BusyCircularProgress2,
  BusyLinearProgress as BusyLinearProgress2
} from "@xylabs/react-shared";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var BusyCard = ({
  busy,
  busyMinimum = 500,
  busyVariant = "circular",
  busyVariantProps,
  children,
  ...props
}) => {
  const internalBusy = useBusyTiming(busy, busyMinimum);
  return /* @__PURE__ */ jsxs2(Card, { ...props, children: [
    children,
    busyVariant === "circular" && internalBusy ? /* @__PURE__ */ jsx2(BusyCircularProgress2, { ...busyVariantProps }) : null,
    busyVariant === "linear" && internalBusy ? /* @__PURE__ */ jsx2(BusyLinearProgress2, { ...busyVariantProps }) : null
  ] });
};

// src/components/FlexCol.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var FlexCol = ({ ref, ...props }) => {
  return /* @__PURE__ */ jsx3(BusyBox, { alignItems: "center", display: "flex", flexDirection: "column", justifyContent: "center", ref, ...props });
};
FlexCol.displayName = "FlexColXYLabs";
var FlexGrowCol = ({ ref, ...props }) => {
  return /* @__PURE__ */ jsx3(FlexCol, { flexGrow: 1, ref, ...props });
};
FlexGrowCol.displayName = "FlexGrowColXYLabs";

// src/components/FlexRow.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
var FlexRow = ({ ref, ...props }) => {
  return /* @__PURE__ */ jsx4(BusyBox, { alignItems: "center", display: "flex", flexDirection: "row", justifyContent: "center", ref, ...props });
};
FlexRow.displayName = "FlexRowXYLabs";
var FlexGrowRow = ({ ref, ...props }) => {
  return /* @__PURE__ */ jsx4(FlexRow, { flexGrow: 1, ref, ...props });
};
FlexGrowRow.displayName = "FlexGrowRowXYLabs";

// src/components/HoverScale.tsx
import { Box as Box2 } from "@mui/material";
import { jsx as jsx5 } from "react/jsx-runtime";
var HoverScale = ({
  scale,
  children,
  ...props
}) => {
  return /* @__PURE__ */ jsx5(FlexRow, { ...props, children: /* @__PURE__ */ jsx5(
    Box2,
    {
      sx: {
        zoomdiv: {
          "&:hover": {
            transform: `scale(${scale})`,
            transitionDuration: "0.2s",
            transitionTimingFunction: "ease"
          }
        }
      },
      children
    }
  ) });
};
export {
  BusyBox,
  BusyCard,
  FlexCol,
  FlexGrowCol,
  FlexGrowRow,
  FlexRow,
  HoverScale,
  useBusyTiming
};
//# sourceMappingURL=index.mjs.map